import { Projection, createProjection } from '../misc/projection';
import type { CanvasRenderingContext } from 'tabview/canvasRendering/types';
import { rectHeight, rectWidth } from '../misc/geometry';
import { renderHandles } from './handles';
import { BlocksViewNode, BlocksViewVisualState, EditPreview } from '../types';
import { renderDragGhost } from './node/node';
import { isGroup } from '../viewModel$/util';
import { Bounds } from '@ardoq/graph';
import {
  BLOCKS_VIEW_ALIGNMENT_LINEDASH,
  BLOCKS_VIEW_ALIGNMENT_STROKE,
  BLOCKS_VIEW_BADDROP_FILL,
  BLOCKS_VIEW_EDITPREVIEW_DROPFILL,
  BLOCKS_VIEW_GROUP_BORDERCOLOR,
  BLOCKS_VIEW_GROUP_BORDERWIDTH,
  BLOCKS_VIEW_LEAF_BORDERCOLOR,
  BLOCKS_VIEW_LEAF_BORDERWIDTH,
  BLOCKS_VIEW_NODE_BORDERRADIUS,
} from '../consts';

let image: ImageBitmap | null = null;
let imageNode: BlocksViewNode | null = null;
const dragImage = (node: BlocksViewNode): ImageBitmap => {
  const size = [rectWidth(node.bounds), rectHeight(node.bounds)];

  if (
    !image ||
    imageNode !== node ||
    image.width !== size[0] ||
    image.height !== size[1]
  ) {
    const canvas = new OffscreenCanvas(size[0], size[1]);
    const ctx = canvas.getContext('2d')!;

    const doit = (node: BlocksViewNode) => {
      const visualState = node.visualState;

      node.visualState = BlocksViewVisualState.None;
      renderDragGhost(node, ctx);
      node.visualState = visualState;

      if (isGroup(node) && node.open) {
        for (const child of node.children) {
          doit(child);
        }
      }
    };

    const xform = createProjection();
    const vp: Bounds = [0, 0, size[0], size[1]];
    xform.adjustToViewport(vp, 1);
    xform.adjustToRect(node.bounds, 0, false);

    ctx.setTransform();
    ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
    ctx.setTransform(xform.matrix());

    doit(node);

    imageNode = node;
    image = canvas.transferToImageBitmap();
  }

  return image!;
};

export const forceNodeCell = (node: BlocksViewNode, bounds: Bounds) => {
  /* stretch the node and its descendancy's cells to fit the specified cell bounds
    so that subsequent transitions start from same positions as those in the
    drag image */

  if (!node.cell) {
    // node doesn't have a cell to animate from

    return;
  }

  /* build an affine scale/translate transform to apply to the node and its descendancy */

  const xy = node.cell;
  const uv = bounds;

  const s = [
    (uv[2] - uv[0]) / (xy[2] - xy[0]),
    (uv[3] - uv[1]) / (xy[3] - xy[1]),
  ];
  const d = [-xy[0] * s[0] + uv[0], -xy[1] * s[1] + uv[1]];

  /* apply transform to the node and its descendancy */

  const apply = (node: BlocksViewNode) => {
    if (node.cell) {
      node.cell[0] = node.cell[0] * s[0] + d[0];
      node.cell[1] = node.cell[1] * s[1] + d[1];
      node.cell[2] = node.cell[2] * s[0] + d[0];
      node.cell[3] = node.cell[3] * s[1] + d[1];
    }

    if (node.children) {
      for (const child of node.children) {
        apply(child);
      }
    }
  };

  apply(node);
};

export const renderEditPreview = (
  editPreview: EditPreview,
  projection: Projection,
  ctx: CanvasRenderingContext
) => {
  const node = editPreview.node;

  if (!node.parent) {
    return;
  }

  const group = node.parent!;

  if (
    editPreview.real[0].length !== 0 ||
    editPreview.real[1].length !== 0 ||
    editPreview.fake[0].length !== 0 ||
    editPreview.fake[1].length !== 0
  ) {
    /* prepare alignment line world-space dashes and width */

    const resolution = projection.resolution(true);
    const lineWidth = resolution;
    const lineDash = [];

    for (const n of BLOCKS_VIEW_ALIGNMENT_LINEDASH) {
      lineDash.push(n * resolution);
    }

    /* draw alignment lines */

    ctx.save();

    ctx.lineWidth = lineWidth;
    ctx.strokeStyle = BLOCKS_VIEW_ALIGNMENT_STROKE;

    ctx.beginPath();

    for (const col of editPreview.real[0]) {
      ctx.moveTo(col, group.bounds[1]);
      ctx.lineTo(col, group.bounds[3]);
    }

    for (const row of editPreview.real[1]) {
      ctx.moveTo(group.bounds[0], row);
      ctx.lineTo(group.bounds[2], row);
    }

    ctx.setLineDash([]);
    ctx.stroke();

    ctx.beginPath();

    for (const col of editPreview.fake[0]) {
      ctx.moveTo(col, group.bounds[1]);
      ctx.lineTo(col, group.bounds[3]);
    }

    for (const row of editPreview.fake[1]) {
      ctx.moveTo(group.bounds[0], row);
      ctx.lineTo(group.bounds[2], row);
    }

    ctx.lineWidth = lineWidth;
    ctx.setLineDash(lineDash);
    ctx.stroke();

    ctx.restore();
  }

  /* draw the drop shadow */

  if (editPreview.drop) {
    ctx.fillStyle = BLOCKS_VIEW_EDITPREVIEW_DROPFILL;
    ctx.fillRect(
      editPreview.drop[0],
      editPreview.drop[1],
      rectWidth(editPreview.drop),
      rectHeight(editPreview.drop)
    );
  }

  /* draw the edit node */

  const bounds = editPreview.bounds;
  const borderWidth = isGroup(editPreview.node)
    ? BLOCKS_VIEW_GROUP_BORDERWIDTH
    : BLOCKS_VIEW_LEAF_BORDERWIDTH;
  const borderColor = isGroup(editPreview.node)
    ? BLOCKS_VIEW_GROUP_BORDERCOLOR
    : BLOCKS_VIEW_LEAF_BORDERCOLOR;

  ctx.globalAlpha = editPreview.isGood ? 1.0 : 0.25;
  ctx.drawImage(
    dragImage(editPreview.node),
    bounds[0],
    bounds[1],
    rectWidth(bounds),
    rectHeight(bounds)
  );

  ctx.beginPath();
  ctx.roundRect(
    bounds[0],
    bounds[1],
    rectWidth(bounds),
    rectHeight(bounds),
    BLOCKS_VIEW_NODE_BORDERRADIUS
  );
  ctx.lineWidth = borderWidth / projection.resolution(true);
  ctx.strokeStyle = borderColor;
  ctx.stroke();

  ctx.globalAlpha = 1.0;

  if (!editPreview.isGood) {
    /* make the bad edit node red */

    ctx.fillStyle = BLOCKS_VIEW_BADDROP_FILL;
    ctx.fillRect(bounds[0], bounds[1], rectWidth(bounds), rectHeight(bounds));
  }

  renderHandles(editPreview.handles, ctx);
};
